import React, { useEffect, useState } from "react";
import Container from "../../ui/container/Container";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import authManager from "../../../utils/authManager";
import { SHOW_FORM_ROUTE } from "../../../routes/web/rootRoutes";
import { 
  FaHeartbeat, 
  FaUsers, 
  FaArrowUp, 
  FaExclamationTriangle,
  FaFileAlt,
  FaMedkit,
  FaUserFriends,
  FaChartLine,
  FaBell,
  FaEye
} from "react-icons/fa";

const FORM_URLS = {
  "DEATH APPLICATION FORM": "deathform",
  "MEDICAL AID APPLICATION FOR FAMILY MEMBER": "medical-aid-family",
  "MEDICAL AID APPLICATION FOR MEMBER": "medical-aid",
  "REGISTRATION (RETIREMENT BENEFIT SCHEME)": "retirement",
  "NOMINATION FORM": "nomination",
  "UPGRADATION MEMBERSHIP APPLICATION FORM": "upgradtion",
  "COMPLAINT - FORM": "complaint",
};

function ShowFormCards() {
  const navigate = useNavigate();
  const forms = useSelector(
    (state) => state.globalState?.siteSetting?.formData
  );

  const [formCounts, setFormCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name"); // 'name' or 'count'

  // Navigate to details
  const showForm = (formType) => {
    if (localStorage.getItem("useAccessToken")) {
      navigate(SHOW_FORM_ROUTE(formType));
    } else {
      navigate("/auth/login");
    }
  };

  // Fetch counts
  const fetchFormCounts = async () => {
    setLoading(true);
    const counts = {};

    try {
      for (const [key, endpoint] of Object.entries(FORM_URLS)) {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}${endpoint}?limit=0`,
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${authManager.getAccessToken()}`,
              },
            }
          );

          counts[key] = res.data?.total || res.data?.data?.length || 0;
        } catch (err) {
          counts[key] = 0;
        }
      }

      setFormCounts(counts);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormCounts();
  }, []);

  // Filter and sort forms
  const visibleForms = forms?.filter(
    (form) => (formCounts[form.type] || 0) > 0
  );

  const filteredAndSortedForms = visibleForms
    ?.filter(form => 
      form.type.toLowerCase().includes(searchTerm.toLowerCase())
    )
    ?.sort((a, b) => {
      if (sortBy === 'name') {
        return a.type.localeCompare(b.type);
      } else {
        return (formCounts[b.type] || 0) - (formCounts[a.type] || 0);
      }
    });

  // UI helpers
  const getFormColor = (type) => {
    if (type.includes("MEDICAL AID APPLICATION FOR MEMBER"))
      return {
        bg: "bg-gradient-to-br from-blue-50 to-blue-100",
        border: "border-blue-200",
        icon: "text-blue-600",
        badge: "bg-blue-600",
        button: "from-blue-600 to-blue-700"
      };
    if (type.includes("MEDICAL AID APPLICATION FOR FAMILY"))
      return {
        bg: "bg-gradient-to-br from-cyan-50 to-cyan-100",
        border: "border-cyan-200",
        icon: "text-cyan-600",
        badge: "bg-cyan-600",
        button: "from-cyan-600 to-cyan-700"
      };
    if (type.includes("REGISTRATION"))
      return {
        bg: "bg-gradient-to-br from-purple-50 to-purple-100",
        border: "border-purple-200",
        icon: "text-purple-600",
        badge: "bg-purple-600",
        button: "from-purple-600 to-purple-700"
      };
    if (type.includes("NOMINATION"))
      return {
        bg: "bg-gradient-to-br from-green-50 to-green-100",
        border: "border-green-200",
        icon: "text-green-600",
        badge: "bg-green-600",
        button: "from-green-600 to-green-700"
      };
    if (type.includes("UPGRADATION"))
      return {
        bg: "bg-gradient-to-br from-amber-50 to-amber-100",
        border: "border-amber-200",
        icon: "text-amber-600",
        badge: "bg-amber-600",
        button: "from-amber-600 to-amber-700"
      };
    if (type.includes("COMPLAINT"))
      return {
        bg: "bg-gradient-to-br from-red-50 to-red-100",
        border: "border-red-200",
        icon: "text-red-600",
        badge: "bg-red-600",
        button: "from-red-600 to-red-700"
      };
    if (type.includes("DEATH"))
      return {
        bg: "bg-gradient-to-br from-gray-50 to-gray-100",
        border: "border-gray-200",
        icon: "text-gray-600",
        badge: "bg-gray-600",
        button: "from-gray-600 to-gray-700"
      };
    return {
      bg: "bg-gradient-to-br from-gray-50 to-gray-100",
      border: "border-gray-200",
      icon: "text-gray-600",
      badge: "bg-gray-600",
      button: "from-gray-600 to-gray-700"
    };
  };

  const getFormIcon = (type) => {
    if (type.includes("MEDICAL AID APPLICATION FOR MEMBER")) 
      return <FaMedkit className="w-5 h-5" />;
    if (type.includes("MEDICAL AID APPLICATION FOR FAMILY")) 
      return <FaUserFriends className="w-5 h-5" />;
    if (type.includes("REGISTRATION")) 
      return <FaFileAlt className="w-5 h-5" />;
    if (type.includes("NOMINATION")) 
      return <FaUsers className="w-5 h-5" />;
    if (type.includes("UPGRADATION")) 
      return <FaChartLine className="w-5 h-5" />;
    if (type.includes("COMPLAINT")) 
      return <FaExclamationTriangle className="w-5 h-5" />;
    if (type.includes("DEATH")) 
      return <FaHeartbeat className="w-5 h-5" />;
    return <FaFileAlt className="w-5 h-5" />;
  };

  const getFormDescription = (type) => {
    if (type.includes("DEATH")) return "Death benefit claim applications";
    if (type.includes("MEDICAL AID APPLICATION FOR MEMBER")) return "Medical aid for member";
    if (type.includes("MEDICAL AID APPLICATION FOR FAMILY")) return "Medical aid for family member";
    if (type.includes("REGISTRATION")) return "Retirement benefit registrations";
    if (type.includes("NOMINATION")) return "Nomination form submissions";
    if (type.includes("UPGRADATION")) return "Membership upgradation requests";
    if (type.includes("COMPLAINT")) return "Complaint registrations";
    return "Form submissions";
  };

  const totalSubmissions = Object.values(formCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Container>
        <div className="max-w-7xl mx-auto py-6 md:py-8 px-4">
          {/* Header with Stats */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  📊 Form Submissions Overview
                </h1>
                <p className="text-xs md:text-sm text-gray-600 mt-1">
                  Track and manage all form submissions
                </p>
              </div>
              
              {/* Total Stats Card */}
              <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 border border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FaFileAlt className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Total Submissions</p>
                    <p className="text-xl md:text-2xl font-bold text-gray-900">
                      {totalSubmissions}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="🔍 Search forms..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSortBy('name')}
                  className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                    sortBy === 'name' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Sort by Name
                </button>
                <button
                  onClick={() => setSortBy('count')}
                  className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                    sortBy === 'count' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Sort by Count
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 md:py-20">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-sm text-gray-600">Loading form data...</p>
            </div>
          ) : filteredAndSortedForms?.length === 0 ? (
            <div className="text-center py-16 md:py-20 bg-white rounded-xl shadow-sm">
              <div className="text-5xl mb-4">📭</div>
              <p className="text-lg md:text-xl font-semibold text-gray-700">
                No Form Submissions Found
              </p>
              <p className="text-xs md:text-sm text-gray-500 mt-2">
                {searchTerm ? 'No matches found for your search' : 'Form submissions will appear here'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 px-4 py-2 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {filteredAndSortedForms.map((form) => {
                const count = formCounts[form.type] || 0;
                const colors = getFormColor(form.type);
                const formIcon = getFormIcon(form.type);
                const description = getFormDescription(form.type);

                return (
                  <div
                    key={form.id}
                    className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                  >
                    {/* Top Accent Bar */}
                    <div className={`h-1.5 w-full ${colors.badge} opacity-80`}></div>

                    {/* Count Badge - More subtle */}
                    <div className={`absolute top-3 right-3 ${colors.badge} text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm`}>
                      {count} {count === 1 ? 'entry' : 'entries'}
                    </div>

                    {/* Header */}
                    <div className={`p-4 ${colors.bg} border-b ${colors.border}`}>
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 bg-white rounded-lg shadow-sm ${colors.icon}`}>
                          {formIcon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-bold text-gray-800 uppercase tracking-wide truncate">
                            {form.type}
                          </h3>
                          <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                            {description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Body - Simplified */}
                    <div className="p-4 bg-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-2xl font-bold text-gray-800">
                            {count}
                          </p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                            Total Submissions
                          </p>
                        </div>
                        
                        {/* Mini Chart / Indicator */}
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-1.5 h-${Math.floor(Math.random() * 6) + 4} rounded-full ${
                                i < Math.ceil(count / 2) ? colors.badge : 'bg-gray-200'
                              } opacity-60`}
                            ></div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                      <button
                        onClick={() => showForm(form.type)}
                        className={`w-full bg-gradient-to-r ${colors.button} text-white text-xs font-medium py-2.5 px-3 rounded-lg hover:shadow-md transition-all flex items-center justify-center gap-2 group`}
                      >
                        <FaEye className="w-3.5 h-3.5 group-hover:scale-110 transition" />
                        View Details
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer Stats */}
          {!loading && filteredAndSortedForms?.length > 0 && (
            <div className="mt-6 md:mt-8 text-center">
              <p className="text-[10px] md:text-xs text-gray-500">
                Showing {filteredAndSortedForms.length} of {visibleForms?.length || 0} forms • 
                Total {totalSubmissions} submissions across all categories
              </p>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}

export default ShowFormCards;